import axios from 'axios';
import { API_BASE_URL } from '../config';
import { getToken } from '../utils/auth';

// Types for user data
export interface User {
  id: number;
  username: string;
  role: 'admin' | 'manager' | 'seller';
  createdAt: string;
  updatedAt: string;
}

export interface UserProfile {
  id: number;
  username: string;
  role: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

export interface CreateUserData {
  username: string;
  password: string;
  role: 'admin' | 'manager' | 'seller';
}

// Get all users (admin only)
export const getAllUsers = async (): Promise<User[]> => {
  try {
    const token = getToken();
    const response = await axios.get(`${API_BASE_URL}/users`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

// Get user profile
export const getUserProfile = async (): Promise<UserProfile> => {
  try {
    const token = getToken();
    const response = await axios.get(`${API_BASE_URL}/users/profile`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

// Update user profile
export const updateUserProfile = async (username: string): Promise<UserProfile> => {
  try {
    const token = getToken();
    const response = await axios.put(
      `${API_BASE_URL}/users/profile`,
      { username },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data.user;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

// Change password
export const changePassword = async (passwordData: ChangePasswordData): Promise<void> => {
  try {
    const token = getToken();
    await axios.put(
      `${API_BASE_URL}/users/change-password`,
      passwordData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
  } catch (error) {
    console.error('Error changing password:', error);
    throw error;
  }
};

// Create user (admin only)
export const createUser = async (userData: CreateUserData): Promise<User> => {
  try {
    const token = getToken();
    const response = await axios.post(
      `${API_BASE_URL}/users`,
      userData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data.user;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

// Delete user (admin only)
export const deleteUser = async (id: number): Promise<void> => {
  try {
    const token = getToken();
    await axios.delete(`${API_BASE_URL}/users/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  } catch (error) {
    console.error(`Error deleting user with id ${id}:`, error);
    throw error;
  }
};

